import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  Policy as PolicyIcon,
  People as TeamIcon,
  Settings as SettingsIcon,
  Business as OrganizationIcon,
  Logout as LogoutIcon,
  Person as ProfileIcon,
  SmartToy as AssistantIcon,
  Quiz as QuestionnaireIcon,
  HealthAndSafety as InsuranceIcon,
  GppGood as ComplianceIcon,
  BugReport as ThreatsIcon,
  WarningAmber as RisksIcon,
  Assessment as BoardReportIcon,
  CreditCard as BillingIcon,
  Build as ToolsIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { BRAND } from '../../lib/brand';
import { getStoredAttribution, isAttributionStamped, markAttributionStamped } from '../../lib/attribution';
import { bootstrapProfile } from '../../lib/onboarding';
import { useAppDispatch, resetStore } from '../../store';
import { fetchOrganizationProfile } from '../../store/slices/organizationSlice';
import type { User } from '@supabase/supabase-js';

interface AppLayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 240;

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [user, setUser] = useState<User | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // See index.html - suppresses the marketing site's decorative grain
  // texture, which doesn't belong on a dense, data-heavy app screen.
  React.useEffect(() => {
    document.body.classList.add('app-shell');
    return () => document.body.classList.remove('app-shell');
  }, []);

  // Belt-and-braces: the menu item's onClick already closes the drawer,
  // but tying it to the route itself means it can never get stuck open
  // after a navigation, regardless of click/unmount timing.
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);

      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('user_id', user.id)
          .maybeSingle();

        let organizationId = profile?.organization_id ?? null;

        if (!organizationId) {
          // Accounts created by the old broken signup/OAuth flows (auth user
          // exists, no profile row) self-heal here on first load. Also
          // covers a user who had a pending team invite queued but landed
          // here before it was consumed.
          const { data: provisioned, error: provisionError } = await bootstrapProfile();
          if (provisionError) {
            console.error('Profile provisioning failed:', provisionError);
          } else {
            organizationId = provisioned?.organization_id ?? null;
          }
        }

        if (organizationId) {
          dispatch(fetchOrganizationProfile(organizationId));
        }

        // One-time signup attribution stamp: if this browser captured UTM
        // touches on the public pages and the org has none recorded yet,
        // persist them. Best-effort - a failure just leaves the field null.
        if (organizationId && !isAttributionStamped()) {
          const attribution = getStoredAttribution();
          if (attribution) {
            const { data: org } = await (supabase as any)
              .from('organizations')
              .select('signup_attribution')
              .eq('id', organizationId)
              .maybeSingle();
            if (org && org.signup_attribution == null) {
              await (supabase as any)
                .from('organizations')
                .update({ signup_attribution: attribution })
                .eq('id', organizationId);
            }
          }
          markAttributionStamped();
        }
      }
    };

    fetchUser();
  }, [dispatch]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      // Clear Supabase session
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Logout error:', error);
        return;
      }

      // Wipe in-memory state so the next login in this tab can't see the
      // previous account's organization/policies/chats. (No localStorage
      // wipe: signOut already removes Supabase's own key, and clear() nuked
      // unrelated keys.)
      dispatch(resetStore());

      // Redirect to login page
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const menuItems = [
    { 
      text: 'Dashboard', 
      icon: <DashboardIcon />, 
      path: '/dashboard' 
    },
    {
      text: 'Policies',
      icon: <PolicyIcon />,
      path: '/policies'
    },
    {
      text: 'Assistant',
      icon: <AssistantIcon />,
      path: '/assistant'
    },
    {
      text: 'Questionnaires',
      icon: <QuestionnaireIcon />,
      path: '/questionnaires'
    },
    {
      text: 'Insurance',
      icon: <InsuranceIcon />,
      path: '/insurance-renewal'
    },
    {
      text: 'Compliance',
      icon: <ComplianceIcon />,
      path: '/compliance'
    },
    {
      text: 'Threats',
      icon: <ThreatsIcon />,
      path: '/threat-matches'
    },
    {
      text: 'Risks',
      icon: <RisksIcon />,
      path: '/risks'
    },
    {
      text: 'Board Report',
      icon: <BoardReportIcon />,
      path: '/board-report'
    },
    {
      text: 'Team',
      icon: <TeamIcon />,
      path: '/team'
    },
    { 
      text: 'Organization', 
      icon: <OrganizationIcon />, 
      path: '/organization' 
    },
    {
      text: 'Billing',
      icon: <BillingIcon />,
      path: '/subscription'
    },
    {
      text: 'Free Tools',
      icon: <ToolsIcon />,
      path: '/tools'
    },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings'
    }
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'white',
          color: 'text.primary'
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton
                aria-label="Open navigation menu"
                edge="start"
                onClick={() => setMobileOpen(true)}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box component="img" src="/logo-mark.svg" alt="" sx={{ width: 24, height: 24 }} />
            <Typography variant="h6" noWrap>
              {BRAND.productName}
            </Typography>
          </Box>
          {user && (
            <Box>
              <IconButton onClick={handleMenuOpen}>
                <Avatar 
                  src={user.user_metadata?.avatar_url || undefined}
                  alt={user.email || ''}
                >
                  {user.email?.[0].toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={() => {
                  navigate('/profile');
                  handleMenuClose();
                }}>
                  <ListItemIcon>
                    <ProfileIcon />
                  </ListItemIcon>
                  Profile
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? 0 : drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            top: isMobile ? 0 : 64 // Height of AppBar - the temporary drawer overlays it instead
          },
        }}
      >
        {!isMobile && <Toolbar />}
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {menuItems.map((item) => (
              <ListItem
                button
                key={item.text}
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          marginTop: '64px',
          marginLeft: isMobile ? 0 : `${drawerWidth}px`,
          maxWidth: '1200px',
          mx: 'auto',
          minWidth: 0
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AppLayout;
